import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import capitalize from 'lodash/capitalize';
import { Breadcrumb } from '@els/els-react--breadcrumb';
import { dpitems } from '@Constants';
import { paths } from '@AppRoutes/constants';
import {
  isBackToQueryVisibleFn,
  isBackToQueryBuilderFn,
  shouldDisplayPreviewResultsBreadcrumbFn,
} from '@Containers/Breadcrumbs/BreadcrumbsSelectors';
import StructureDataContainer from '@Containers/Breadcrumbs/BreadcrumbTooltip/StructureData';
import { recordCountOrTotalResultFromBreadcrumb } from '@Containers/PatentFamilyClustering/selectors';
import { formatNumberWithPostfix, replaceCitations } from '@Utils/String';
import { BACK_TO_QUERY_QUICK_SEARCH_ACTIONS } from '@Containers/Breadcrumbs/BreadcrumbsConstants';
import { generateHeatmapPath, generateResultsPath } from '@AppRoutes/utils';

import './ResultsBreadcrumbs.scss';

const FlyoutPlacements = {
  BOTTOM_START: 'bottom-start',
};

// Just label
function getBreadcrumbLabel(breadcrumb) {
  if (breadcrumb.get('isHistoryBreadcrumb')) return 'From history';
  if (breadcrumb.get('batchSearch')) return 'Batch Load';

  const totalResults = recordCountOrTotalResultFromBreadcrumb(breadcrumb) || 0;
  const contextLabel = capitalize(replaceCitations(breadcrumb.get('context', '')));
  return `${formatNumberWithPostfix(totalResults, true, 2)} ${contextLabel}`;
}

const makeFlyoutProps = (flyout, trigger = 'hover') => ({
  flyout,
  trigger,
  shouldCloseOnESC: true,
  pointer: true,
  pointerType: 'primary',
  placement: FlyoutPlacements.BOTTOM_START,
});

function getBreadcrumbHref(breadcrumb) {
  return breadcrumb.get('isHeatmapShown')
    ? generateHeatmapPath({
        ...breadcrumb.toJS(),
        resultName: breadcrumb.get('heatmapResultName'),
        context: dpitems,
        isHeatmap: true,
      })
    : generateResultsPath(breadcrumb.toJS());
}

// Shared builder for all link-type breadcrumb items
function makeLinkItem({ children, onClick, href, automationId }, flyout) {
  return {
    type: 'link',
    linkElementProps: {
      children,
      className: 'c-els-color-info',
      onClick,
      href,
      routerComponent: Link,
      'data-automationid': automationId,
    },
    ...(flyout && { flyoutProps: makeFlyoutProps(flyout) }),
  };
}

// The current breadcrumb is a non-navigation trigger for details, styled to match the surrounding breadcrumb links.
function renderCurrentItem(breadcrumb, flyout) {
  return {
    type: 'button',
    label: getBreadcrumbLabel(breadcrumb),
    flyoutProps: makeFlyoutProps(flyout, 'click'),
  };
}

function renderLinkItem(breadcrumb, goToResultPage, flyout) {
  return makeLinkItem(
    {
      children: getBreadcrumbLabel(breadcrumb),
      onClick: (e) => {
        e.preventDefault();
        goToResultPage(breadcrumb.toJS());
      },
      href: getBreadcrumbHref(breadcrumb),
      automationId: 'breadcrumb-link',
    },
    flyout
  );
}

function renderPreviewResultsItem(breadcrumb, backToQueryHandler) {
  return makeLinkItem({
    children: 'Preview results',
    onClick: (e) => {
      e.preventDefault();
      backToQueryHandler(breadcrumb.toJS(), BACK_TO_QUERY_QUICK_SEARCH_ACTIONS.PREVIEW);
    },
    href: paths.PREVIEW,
    automationId: 'breadcrumb-preview-results',
  });
}

function renderSearchItem(breadcrumb, backToQueryHandler, isBackToQueryBuilder, flyout) {
  return makeLinkItem(
    {
      children: 'Search',
      onClick: (e) => {
        e.preventDefault();
        backToQueryHandler(breadcrumb.toJS(), BACK_TO_QUERY_QUICK_SEARCH_ACTIONS.EDIT);
      },
      href: isBackToQueryBuilder ? paths.ADVANCED : paths.SEARCH,
      automationId: 'breadcrumb-search',
    },
    flyout
  );
}

function Breadcrumbs({ breadcrumbsIds, breadcrumbsItems, goToResultPage, backToQueryHandler }) {
  const breadcrumbsData = useMemo(() => {
    if (!breadcrumbsIds || !breadcrumbsItems) return [];

    const breadcrumbsArr = breadcrumbsIds.toJS().map((id) => {
      const breadcrumb = breadcrumbsItems.get(id);
      const flyout = <StructureDataContainer breadcrumb={breadcrumb} />;
      return breadcrumb?.get('active') ? renderCurrentItem(breadcrumb, flyout) : renderLinkItem(breadcrumb, goToResultPage, flyout);
    });

    const firstBreadcrumb = breadcrumbsItems.get(breadcrumbsIds.first());

    if (firstBreadcrumb && isBackToQueryVisibleFn(firstBreadcrumb)) {
      if (shouldDisplayPreviewResultsBreadcrumbFn(firstBreadcrumb)) {
        breadcrumbsArr.unshift(renderPreviewResultsItem(firstBreadcrumb, backToQueryHandler));
      }
      breadcrumbsArr.unshift(
        renderSearchItem(
          firstBreadcrumb,
          backToQueryHandler,
          isBackToQueryBuilderFn(firstBreadcrumb),
          <StructureDataContainer breadcrumb={firstBreadcrumb} />
        )
      );
    }

    return breadcrumbsArr;
  }, [breadcrumbsIds, breadcrumbsItems, goToResultPage, backToQueryHandler]);

  return (
    <Breadcrumb
      className="u-els-padding-top u-els-padding-bottom u-els-padding-left-2x u-els-padding-right-2x u-els-background-color-n0 u-els-font-size-base results-breadcrumbs"
      itemData={breadcrumbsData}
    />
  );
}

export default Breadcrumbs;
